// DAO for company_bank_accounts — counterparty IBAN learning.
//
// The table predates this matcher and is shared with the manual
// company-bank-account UI. We use the `account_number` column as the
// IBAN (mBank uses NRB which is the IBAN sans country prefix; both
// shapes are accepted). Learning columns added by migration v2:
//   - match_source ∈ ('observed_match','invoice_seller_field','manual')
//   - match_count INT
//   - last_matched_at TIMESTAMPTZ
//
// On every successful auto-match where the bank entry carried a
// counterparty IBAN, the matcher emits an observe(): if the IBAN
// already exists for the company, bump match_count + last_matched_at;
// otherwise insert a new row tagged 'observed_match'.

import { BaseDao, type InvalidateFn, type SupabaseClient } from '../../base.ts';
import { ibanCore, canonicalAccountNumber, normalizeIban } from '../../../utils/iban.ts';

export type CompanyBankAccountSource =
  | 'observed_match'
  | 'invoice_seller_field'
  | 'manual';

export interface CompanyBankAccountRow {
  id: number;
  company_id: number;
  account_number: string;
  match_count: number;
  last_matched_at: string | null;
  match_source: CompanyBankAccountSource | null;
}

/** Minimal registry-row projection for the invoice bank-account correction gate
 *  (InvoiceAccountAssignService, R2): which company the row belongs to (must match
 *  the invoice's counterparty) + its trust tier (must clear the payable-or-whitelisted
 *  bar before an operator can assign it to an invoice). */
export interface CompanyBankAccountAssignRow {
  id: number;
  company_id: number;
  account_number: string;
  verification_status: string | null;
  white_list_status: boolean | null;
}

export class CompanyBankAccountDao extends BaseDao<CompanyBankAccountRow> {
  protected readonly dependentResources = ['company_bank_accounts'];

  constructor(sb: SupabaseClient, invalidate?: InvalidateFn) {
    super(sb, 'company_bank_accounts', invalidate);
  }

  /** Single registry row by id, projected for the invoice bank-account correction gate
   *  (R2). Null when the id doesn't exist. */
  async getForAssignment(id: number): Promise<CompanyBankAccountAssignRow | null> {
    const { data, error } = await this.sb
      .from('company_bank_accounts')
      .select('id, company_id, account_number, verification_status, white_list_status')
      .eq('id', id)
      .maybeSingle();
    if (error) throw error;
    return data as CompanyBankAccountAssignRow | null;
  }

  /** All known account numbers for the given company ids. */
  async findByCompanyIds(
    companyIds: number[],
  ): Promise<Array<{ company_id: number; iban: string }>> {
    if (companyIds.length === 0) return [];
    const { data, error } = await this.sb
      .from('company_bank_accounts')
      .select('company_id, account_number')
      .in('company_id', companyIds);
    if (error) throw error;
    type Row = { company_id: number; account_number: string };
    return ((data ?? []) as Row[]).map((r) => ({
      company_id: r.company_id,
      iban: normalizeIban(r.account_number ?? ''),
    }));
  }

  /** Payable account cores per vendor (payable set = payment_verified/operator_verified),
   *  values ibanCore-normalized. Mirror of InvoiceDao.getVerifiedAccountCores — kept here
   *  so the outbound tree needs no cross-tree DAO import. */
  async findVerifiedCores(companyIds: number[]): Promise<Map<number, Set<string>>> {
    if (companyIds.length === 0) return new Map();
    const { data, error } = await this.sb
      .from('company_bank_accounts')
      .select('company_id, account_number')
      .in('company_id', companyIds)
      .in('verification_status', ['payment_verified', 'operator_verified']);
    if (error) throw error;
    const m = new Map<number, Set<string>>();
    for (const r of (data ?? []) as { company_id: number; account_number: string }[]) {
      const set = m.get(r.company_id) ?? new Set<string>();
      set.add(ibanCore(r.account_number));
      m.set(r.company_id, set);
    }
    return m;
  }

  /**
   * Idempotent observe. One DB roundtrip via PostgreSQL upsert on the
   * `(company_id, account_number)` natural key. When the row exists it
   * bumps `match_count` + `last_matched_at` and backfills any detail
   * fields (swift_code, bank_name) that were null. When absent it
   * inserts a full row.
   *
   * `currency` is required: every caller observes from a bank line,
   * which always carries one. swift_code (BIC) lands when camt.053 is
   * the source — MT940 doesn't carry it. bank_name is best-effort.
   *
   * NOTE: existing data may store account_number with spaces (display
   * form). The unique key `(company_id, account_number)` therefore
   * treats normalized vs spaced as different rows. We normalize before
   * sending — observed values are always written in canonical form
   * (see canonicalAccountNumber, THE storage format for this column).
   */
  async observe(
    companyId: number,
    iban: string,
    currency: string,
    extras: { swiftCode?: string | null; bankName?: string | null } = {},
    source: CompanyBankAccountSource = 'observed_match',
  ): Promise<void> {
    const norm = normalizeIban(iban);
    const canonical = canonicalAccountNumber(iban) ?? norm;
    const ccy = currency.toUpperCase();
    const now = new Date().toISOString();

    const { error } = await this.sb.rpc('observe_company_bank_account', {
      p_company_id: companyId,
      p_account_number: canonical,
      p_source: source,
      p_now: now,
      p_currency: ccy,
      p_swift_code: extras.swiftCode ?? null,
      p_bank_name: extras.bankName ?? null,
    });
    if (error) {
      // Fallback for envs that haven't installed the RPC yet — keeps
      // matching working even if the DB migration is behind.
      const { data: existing } = await this.sb
        .from('company_bank_accounts')
        .select('id, account_number, match_count, swift_code, bank_name')
        .eq('company_id', companyId);
      type Row = {
        id: number;
        account_number: string;
        match_count: number | null;
        swift_code: string | null;
        bank_name: string | null;
      };
      const hit = ((existing ?? []) as Row[]).find(
        (r) => normalizeIban(r.account_number ?? '') === norm,
      );
      if (hit) {
        await this.sb
          .from('company_bank_accounts')
          .update({
            last_matched_at: now,
            match_count: (hit.match_count ?? 0) + 1,
            swift_code: hit.swift_code ?? extras.swiftCode ?? null,
            bank_name: hit.bank_name ?? extras.bankName ?? null,
          })
          .eq('id', hit.id);
      } else {
        await this.sb.from('company_bank_accounts').insert({
          company_id: companyId,
          account_number: canonical,
          currency: ccy,
          swift_code: extras.swiftCode ?? null,
          bank_name: extras.bankName ?? null,
          match_source: source,
          match_count: 1,
          last_matched_at: now,
        });
      }
    }
    this.handleInvalidation();
  }

  /**
   * Payment-evidence promotion: a settled OUTGOING (debit) statement line
   * proves we paid this account, so it climbs to the top trust rung. Called
   * by the matcher services (service-role paths only — trust decisions never
   * ride caller-supplied parameters). Terminal states survive: an operator's
   * rejected/superseded verdict is never overturned by bank evidence, and an
   * already-set verified_at (first evidence) is preserved.
   *
   * Matches by ibanCore (country-code-agnostic) rather than an exact
   * account_number equality, so a spaced/bare/prefixed variant of the same
   * stored row still resolves. Every same-core row for the company is
   * promoted, not just the first — historic data has duplicate rows for the
   * same account stored under different raw formats.
   */
  async promoteToPaymentVerified(companyId: number, iban: string): Promise<void> {
    const core = ibanCore(iban);
    const { data, error } = await this.sb
      .from('company_bank_accounts')
      .select('id, account_number, verification_status, verified_at')
      .eq('company_id', companyId);
    if (error) throw error;
    type Row = {
      id: number;
      account_number: string;
      verification_status: string;
      verified_at: string | null;
    };
    const matches = ((data ?? []) as Row[]).filter((r) => ibanCore(r.account_number) === core);
    let promoted = false;
    for (const row of matches) {
      if (row.verification_status === 'rejected' || row.verification_status === 'superseded') {
        continue;
      }
      if (row.verification_status === 'payment_verified') continue;
      const patch: Record<string, unknown> = {
        verification_status: 'payment_verified',
        status_reason: null,
      };
      if (!row.verified_at) patch.verified_at = new Date().toISOString();
      const { error: upErr } = await this.sb
        .from('company_bank_accounts')
        .update(patch)
        .eq('id', row.id);
      if (upErr) throw upErr;
      promoted = true;
    }
    if (promoted) this.handleInvalidation();
  }

  /**
   * Transfer evidence in EITHER direction fills a missing currency — currency
   * assignment is orthogonal to verification (2026-07-15 correction 3).
   * is_default rows are excluded: filling a display-default's currency could
   * collide with the default-per-currency unique index; defaults with unknown
   * currency stay operator territory (verification queue).
   *
   * Matches by ibanCore, same rationale as promoteToPaymentVerified — every
   * same-core, null-currency, non-default row for the company is filled.
   */
  async fillCurrencyFromTransfer(
    companyId: number,
    iban: string,
    currency: string,
  ): Promise<void> {
    const core = ibanCore(iban);
    const { data, error } = await this.sb
      .from('company_bank_accounts')
      .select('id, account_number, currency, is_default')
      .eq('company_id', companyId);
    if (error) throw error;
    type Row = {
      id: number;
      account_number: string;
      currency: string | null;
      is_default: boolean | null;
    };
    const matches = ((data ?? []) as Row[]).filter(
      (r) => ibanCore(r.account_number) === core && r.currency == null && r.is_default !== true,
    );
    const ccy = currency.toUpperCase();
    for (const row of matches) {
      const { error: upErr } = await this.sb
        .from('company_bank_accounts')
        .update({ currency: ccy })
        .eq('id', row.id);
      if (upErr) throw upErr;
    }
  }

  /** Every registry row, projected for BankAccountDuplicatePurgeService. Pages
   *  past PostgREST's 1000-row cap, ordered by id so pages don't drift. */
  async listPurgeCandidates(): Promise<Array<{
    id: number;
    company_id: number;
    account_number: string;
    verification_status: string;
    status_reason: string | null;
  }>> {
    type Row = {
      id: number;
      company_id: number;
      account_number: string;
      verification_status: string;
      status_reason: string | null;
    };
    const pageSize = 1000;
    const rows: Row[] = [];
    for (let from = 0; ; from += pageSize) {
      const { data, error } = await this.sb
        .from('company_bank_accounts')
        .select('id, company_id, account_number, verification_status, status_reason')
        .order('id')
        .range(from, from + pageSize - 1);
      if (error) throw error;
      const page = (data ?? []) as Row[];
      rows.push(...page);
      if (page.length < pageSize) break;
    }
    return rows;
  }

  /** Hard-delete registry rows by id, chunked to keep the `in` filter short. */
  async deleteByIds(ids: number[]): Promise<void> {
    const chunkSize = 200;
    for (let i = 0; i < ids.length; i += chunkSize) {
      const { error } = await this.sb
        .from('company_bank_accounts')
        .delete()
        .in('id', ids.slice(i, i + chunkSize));
      if (error) throw error;
    }
  }
}
